/**
 * SharedPage Component
 *
 * Public page for viewing shared content. Handles:
 * - Loading share metadata
 * - Access gate (password/email entry)
 * - Displaying content once access is granted
 *
 * URL: /share/:type/:shareId
 * Example: /share/scenario/abc123
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    Box,
    TextField,
    Button,
    Alert,
    CircularProgress,
    InputAdornment,
    Divider,
    CssBaseline,
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import {
    Lock as LockIcon,
    Email as EmailIcon,
    ErrorOutline as ErrorIcon,
} from '@mui/icons-material';
import {
    getShareMeta,
    validateShareAccess,
    getShareData,
} from '../services/shareService';
import { ScenarioCompareModalV2, CustomerProposalView } from '@vortx/site-dna-web';
import { vortxColors, getComponentOverrides, vortxTypography } from '@vortx/shared-ui';
import { debug } from '@vortx/shared-ui';

// Create dark theme for shared pages (matches retro mode)
const sharedPageTheme = createTheme({
    palette: {
        mode: 'dark',
        primary: { main: vortxColors.gold },
        secondary: { main: vortxColors.teal },
        background: {
            default: '#1a1a1a',
            paper: '#242424',
        },
        text: {
            primary: vortxColors.white,
            secondary: vortxColors.stone,
        },
    },
    typography: {
        fontFamily: vortxTypography.body,
        h1: { fontFamily: vortxTypography.heading, fontWeight: 800, color: vortxColors.gold },
        h2: { fontFamily: vortxTypography.heading, fontWeight: 700, color: vortxColors.gold },
        h3: { fontFamily: vortxTypography.heading, fontWeight: 700, color: vortxColors.gold },
        h4: { fontFamily: vortxTypography.heading, fontWeight: 600, color: vortxColors.white },
        h5: { fontFamily: vortxTypography.heading, fontWeight: 600, color: vortxColors.white },
        h6: { fontFamily: vortxTypography.heading, fontWeight: 600, color: vortxColors.white },
    },
    shape: { borderRadius: 8 },
    components: getComponentOverrides('retro'),
});

/**
 * VortxLogo Component
 * Displays the branded logo with gold "VORTX" + light "FLUX"
 */
function VortxLogo({ size = 'medium' }) {
    const sizes = {
        small: { fontSize: '1.25rem', gap: 0.5 },
        medium: { fontSize: '1.5rem', gap: 0.75 },
        large: { fontSize: '2rem', gap: 1 },
    };
    const { fontSize, gap } = sizes[size] || sizes.medium;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap }}>
            <Typography
                component="span"
                sx={{
                    fontSize,
                    fontFamily: vortxTypography.heading,
                    fontWeight: 700,
                    color: vortxColors.gold,
                    letterSpacing: '0.1em',
                }}
            >
                VORTX
            </Typography>
            <Typography
                component="span"
                sx={{
                    fontSize,
                    fontFamily: vortxTypography.heading,
                    fontWeight: 300,
                    color: vortxColors.stone,
                    letterSpacing: '0.1em',
                }}
            >
                FLUX
            </Typography>
        </Box>
    );
}

/**
 * PoweredByFooter Component
 * Shows "Powered by Vortx Flux" branding at the bottom
 */
function PoweredByFooter() {
    return (
        <Box
            sx={{
                mt: 4,
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
                textAlign: 'center',
            }}
        >
            <Typography variant="caption" color="text.secondary">
                Powered by{' '}
                <Typography
                    component="span"
                    variant="caption"
                    sx={{ color: vortxColors.gold, fontWeight: 600, fontFamily: vortxTypography.heading }}
                >
                    Vortx
                </Typography>{' '}
                <Typography
                    component="span"
                    variant="caption"
                    sx={{ color: vortxColors.stone, fontFamily: vortxTypography.heading }}
                >
                    Flux
                </Typography>
            </Typography>
        </Box>
    );
}

/**
 * LoadingState Component
 * Shows a centered spinner while loading
 */
function LoadingState({ message = 'Loading...' }) {
    return (
        <Container maxWidth="sm" sx={{ mt: 8 }}>
            <Paper
                sx={{
                    p: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                }}
            >
                <VortxLogo />
                <CircularProgress size={40} sx={{ color: vortxColors.gold }} />
                <Typography color="text.secondary">{message}</Typography>
            </Paper>
        </Container>
    );
}

/**
 * ErrorState Component
 * Shows error message for not found, expired, or revoked shares
 */
function ErrorState({ error }) {
    return (
        <Container maxWidth="sm" sx={{ mt: 8 }}>
            <Paper
                sx={{
                    p: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                }}
            >
                <VortxLogo />
                <ErrorIcon sx={{ fontSize: 64, color: 'error.main' }} />
                <Typography variant="h5" align="center">
                    Unable to Access Page
                </Typography>
                <Typography variant="body1" align="center" color="text.secondary">
                    {error}
                </Typography>
                <PoweredByFooter />
            </Paper>
        </Container>
    );
}

/**
 * AccessGate Component
 * Form for entering password and/or email to access protected content
 */
function AccessGate({
    meta,
    password,
    setPassword,
    email,
    setEmail,
    accessError,
    accessLoading,
    onSubmit,
}) {
    const requiresPassword = meta?.access?.require_password || false;
    const requiresEmail = meta?.access?.require_email || false;

    return (
        <Container maxWidth="xs" sx={{ mt: 8 }}>
            <Paper
                sx={{
                    p: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 3,
                }}
            >
                <VortxLogo size="large" />

                <Divider sx={{ width: '100%' }} />

                <Typography variant="h6" align="center">
                    {meta?.title || 'Shared Page'}
                </Typography>

                {meta?.description && (
                    <Typography variant="body2" color="text.secondary" align="center">
                        {meta.description}
                    </Typography>
                )}

                <Typography variant="body2" color="text.secondary" align="center">
                    {requiresPassword && requiresEmail
                        ? 'Enter your email and password to view this page.'
                        : requiresPassword
                        ? 'Enter the password to view this page.'
                        : requiresEmail
                        ? 'Enter your email to view this page.'
                        : 'Click below to view this page.'}
                </Typography>

                {accessError && (
                    <Alert severity="error" sx={{ width: '100%' }}>
                        {accessError}
                    </Alert>
                )}

                <form
                    onSubmit={onSubmit}
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 16,
                        width: '100%',
                    }}
                >
                    {requiresEmail && (
                        <TextField
                            label="Email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <EmailIcon color="action" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    )}

                    {requiresPassword && (
                        <TextField
                            label="Password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockIcon color="action" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    )}

                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={accessLoading}
                        sx={{
                            mt: 1,
                            bgcolor: vortxColors.gold,
                            '&:hover': { bgcolor: '#9e7940' },
                        }}
                    >
                        {accessLoading ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            'View Page'
                        )}
                    </Button>
                </form>

                <PoweredByFooter />
            </Paper>
        </Container>
    );
}

/**
 * CollectionContent Component
 * Renders a project collection with project-level tabs, each showing a full proposal view.
 */
function CollectionContent({ meta, data }) {
    const [activeProject, setActiveProject] = useState(0);
    const projects = data?.projects || [];

    if (projects.length === 0) {
        return (
            <Container maxWidth="md" sx={{ mt: 6, textAlign: 'center' }}>
                <Typography color="text.secondary">
                    No projects available in this collection
                </Typography>
            </Container>
        );
    }

    const activeData = projects[activeProject];
    const snapshotData = activeData?.snapshot_data || {};
    const proposalConfig = snapshotData.proposal_config || {};

    return (
        <Container maxWidth="md" sx={{ py: { xs: 3, sm: 5 } }}>
            {/* Collection header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography
                    variant="h5"
                    sx={{ fontWeight: 600, color: vortxColors.white }}
                >
                    {meta?.title || data?.title || 'Project Collection'}
                </Typography>
            </Box>

            {/* Project tab bar */}
            <Box sx={{ display: 'flex', gap: 1, mb: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                {projects.map((p, i) => (
                    <Button
                        key={i}
                        onClick={() => setActiveProject(i)}
                        variant={activeProject === i ? 'contained' : 'outlined'}
                        size="small"
                        sx={{
                            borderRadius: 3,
                            textTransform: 'none',
                            fontWeight: activeProject === i ? 600 : 400,
                            bgcolor: activeProject === i ? vortxColors.gold : 'transparent',
                            color: activeProject === i ? '#1a1a1a' : vortxColors.stone,
                            borderColor: activeProject === i ? vortxColors.gold : vortxColors.stone,
                            '&:hover': {
                                bgcolor: activeProject === i ? '#9e7940' : 'rgba(255,255,255,0.05)',
                                borderColor: vortxColors.gold,
                            },
                            px: 2,
                        }}
                    >
                        {p.label || p.title || `Project ${i + 1}`}
                    </Button>
                ))}
            </Box>

            {/* Active project's proposal */}
            <CustomerProposalView
                scenarios={snapshotData.scenarios || []}
                scenarioNames={proposalConfig.scenario_names || (snapshotData.scenarios || []).map((s, i) => s?.ui_state?.form?.name || s?.name || `Option ${i + 1}`)}
                customerName={proposalConfig.customer_name}
                projectName={activeData.label || activeData.title}
                dealModeFilter={proposalConfig.deal_modes || null}
                createdAt={data?.created_at || meta?.created_at}
                recommendedIndex={proposalConfig.recommended_index ?? null}
                recommendationText={proposalConfig.recommendation_text || null}
            />

            <PoweredByFooter />
        </Container>
    );
}

/**
 * SharedContent Component
 * Displays the actual shared content based on type
 */
function SharedContent({ type, meta, data }) {
    // Check if this is a collection
    if (data?.type === 'project_collection') {
        return <CollectionContent meta={meta} data={data} />;
    }

    // Check if this is a proposal view (new URL type or legacy allowed_views check)
    const isProposal = type === 'proposal' || data?.allowed_views?.includes('proposal');

    if (isProposal) {
        return <ProposalContent meta={meta} data={data} />;
    }

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {/* Header */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 2,
                    }}
                >
                    <Typography variant="h5" sx={{ flex: 1, textAlign: 'center' }}>
                        {meta?.title || 'Shared Content'}
                    </Typography>
                </Box>
            </Paper>

            {/* Content Area */}
            {(type === 'scenario' || type === 'comparison') ? (
                <ScenarioContent meta={meta} data={data} />
            ) : (
                <Paper sx={{ p: 3, minHeight: 400 }}>
                    <GenericContent meta={meta} data={data} />
                </Paper>
            )}

            {/* Footer */}
            <Box sx={{ mt: 3 }}>
                <PoweredByFooter />
            </Box>
        </Container>
    );
}

/**
 * ProposalContent Component
 * Renders the customer proposal view from share data.
 * Supports:
 *  - Snapshot mode: scenarios + proposal_config inside snapshot_data blob
 *  - Live mode: scenarios fetched fresh from Firestore, proposal_config on top-level share doc
 *  - Legacy single-scenario format (scenario_index instead of scenario_names)
 */
function ProposalContent({ meta, data }) {
    const isLive = data?.data_mode === 'live';

    // proposal_config: live mode stores it at top level; snapshot embeds it in blob
    const proposalConfig = isLive
        ? (data?.proposal_config || {})
        : (data?.snapshot_data?.proposal_config || data?.proposal_config || {});

    // scenarios: live mode fetches from data.data.scenarios; snapshot from blob
    const scenarios = isLive
        ? (data?.data?.scenarios || [])
        : (data?.snapshot_data?.scenarios || []);

    // project name
    const projectName = isLive
        ? (data?.data?.project?.name || meta?.title)
        : (data?.snapshot_data?.projectName || meta?.title);

    const customerName = proposalConfig.customer_name;
    const dealModeFilter = proposalConfig.deal_modes || null;
    const createdAt = data?.created_at || meta?.created_at;
    const recommendedIndex = proposalConfig.recommended_index ?? null;
    const recommendationText = proposalConfig.recommendation_text || null;

    // Legacy single-scenario support (proposal_config has scenario_index instead of scenario_names)
    if (!proposalConfig.scenario_names && proposalConfig.scenario_index != null) {
        const idx = proposalConfig.scenario_index;
        const scenario = scenarios[idx];
        if (!scenario) {
            return (
                <Container maxWidth="md" sx={{ mt: 6, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                        No scenario data available for proposal
                    </Typography>
                </Container>
            );
        }
        return (
            <CustomerProposalView
                scenarios={[scenario]}
                scenarioNames={['Option 1']}
                customerName={customerName}
                projectName={projectName}
                dealModeFilter={dealModeFilter}
                createdAt={createdAt}
            />
        );
    }

    // Legacy flat array format
    if (scenarios.length === 0 && Array.isArray(data?.snapshot_data) && data.snapshot_data.length > 0) {
        return (
            <CustomerProposalView
                scenarios={[data.snapshot_data[0]]}
                scenarioNames={['Option 1']}
                projectName={meta?.title}
                dealModeFilter={dealModeFilter}
                createdAt={createdAt}
            />
        );
    }

    if (scenarios.length === 0) {
        return (
            <Container maxWidth="md" sx={{ mt: 6, textAlign: 'center' }}>
                <Typography color="text.secondary">
                    No scenario data available for proposal
                </Typography>
            </Container>
        );
    }

    const scenarioNames = proposalConfig.scenario_names || scenarios.map((s, i) => s?.ui_state?.form?.name || s?.name || `Option ${i + 1}`);

    return (
        <CustomerProposalView
            scenarios={scenarios}
            scenarioNames={scenarioNames}
            customerName={customerName}
            projectName={projectName}
            dealModeFilter={dealModeFilter}
            createdAt={createdAt}
            recommendedIndex={recommendedIndex}
            recommendationText={recommendationText}
        />
    );
}

/**
 * ScenarioContent Component
 * Displays scenario comparison using the full ScenarioCompareModalV2 component in embedded mode
 */
function ScenarioContent({ meta, data }) {
    // Extract raw scenarios - ScenarioCompareModalV2 expects full scenario objects
    let scenarios = [];
    let projectName = meta?.title;

    if (data?.data_mode === 'live' && data?.data?.scenarios) {
        // Live mode - raw scenarios from data.data.scenarios
        scenarios = data.data.scenarios;
        projectName = data.data.project?.name || meta?.title;
    } else if (Array.isArray(data?.snapshot_data)) {
        // Snapshot mode - raw scenarios array
        scenarios = data.snapshot_data;
    } else if (data?.snapshot_data?.scenarios) {
        // Snapshot mode with wrapped format
        scenarios = data.snapshot_data.scenarios;
        projectName = data.snapshot_data.projectName || meta?.title;
    }

    // Extract allowed views from share data
    const allowedViews = data?.allowed_views;

    if (!scenarios || scenarios.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">
                    No scenario data available
                </Typography>
            </Box>
        );
    }

    // Use ScenarioCompareModalV2 in embedded mode
    return (
        <ScenarioCompareModalV2
            scenarios={scenarios}
            projectName={projectName}
            embedded={true}
            allowedViews={allowedViews}
        />
    );
}

/**
 * GenericContent Component
 * Fallback for unsupported content types
 */
function GenericContent({ meta, data }) {
    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                {meta?.resource_type || 'Shared Content'}
            </Typography>

            <Box
                sx={{
                    mt: 2,
                    p: 2,
                    bgcolor: 'grey.100',
                    borderRadius: 1,
                    overflow: 'auto',
                    maxHeight: 500,
                }}
            >
                <pre style={{ margin: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify(data, null, 2)}
                </pre>
            </Box>
        </Box>
    );
}

/**
 * SharedPage Component
 * Main component that handles the full flow:
 * 1. Load metadata
 * 2. Show access gate if required
 * 3. Display content once access is granted
 */
// Cache share data in sessionStorage to survive page refreshes
const SHARE_CACHE_PREFIX = 'vortx_share_data_';

function getCachedShareData(shareId) {
    try {
        const raw = sessionStorage.getItem(SHARE_CACHE_PREFIX + shareId);
        if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return null;
}

function setCachedShareData(shareId, data) {
    try {
        sessionStorage.setItem(SHARE_CACHE_PREFIX + shareId, JSON.stringify(data));
    } catch { /* ignore — quota exceeded etc. */ }
}

export default function SharedPage() {
    const { type, shareId } = useParams();

    // Check for cached data from a previous load in this browser session
    const cached = getCachedShareData(shareId);

    // Main state
    const [meta, setMeta] = useState(null);
    const [data, setData] = useState(cached);
    const [, setViewId] = useState(null); // viewId stored for potential future use (analytics)
    const [, setAccessToken] = useState(null); // Token for browsers that block cross-site cookies
    const [loading, setLoading] = useState(!cached);
    const [error, setError] = useState(null);
    const [accessGranted, setAccessGranted] = useState(!!cached);

    // Access gate state
    const [password, setPassword] = useState('');
    const [email, setEmail] = useState('');
    const [accessError, setAccessError] = useState(null);
    const [accessLoading, setAccessLoading] = useState(false);

    // Auto-validate for public shares (no password/email required)
    const handleAutoValidate = useCallback(async () => {
        try {
            setAccessLoading(true);
            const accessResult = await validateShareAccess(shareId);
            setViewId(accessResult.view_id);
            const token = accessResult.access_token || null;
            setAccessToken(token);

            // Load the full data — pass token as header fallback for cross-site cookie blocking
            const dataResult = await getShareData(shareId, token);
            setData(dataResult);
            setCachedShareData(shareId, dataResult);
            setAccessGranted(true);
        } catch (err) {
            debug.error('Auto-validation failed:', err);
            setError(err.message || 'Access denied');
        } finally {
            setAccessLoading(false);
        }
    }, [shareId]);

    // Load share metadata on mount (skip if cached data is available from sessionStorage)
    useEffect(() => {
        if (accessGranted && data) return; // Already have cached data — skip network requests

        async function loadMeta() {
            if (!shareId) {
                setError('Invalid share link');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                const metaResult = await getShareMeta(shareId);
                setMeta(metaResult);

                // If no password or email required, auto-validate
                if (!metaResult.access?.require_password && !metaResult.access?.require_email) {
                    await handleAutoValidate();
                } else {
                    // Share requires credentials — try fetching data with existing cookie
                    // (user may have already validated in this browser session)
                    try {
                        const dataResult = await getShareData(shareId);
                        setData(dataResult);
                        setCachedShareData(shareId, dataResult);
                        setAccessGranted(true);
                    } catch {
                        // Cookie expired or missing — show access gate
                    }
                }
            } catch (err) {
                debug.error('Failed to load share metadata:', err);
                setError(err.message || 'Failed to load page');
            } finally {
                setLoading(false);
            }
        }

        loadMeta();
    }, [shareId, accessGranted, data, handleAutoValidate]);

    // Handle access form submission
    const handleAccessSubmit = async (e) => {
        e.preventDefault();
        setAccessError(null);
        setAccessLoading(true);

        try {
            // Validate access with credentials
            const accessResult = await validateShareAccess(
                shareId,
                password || null,
                email || null
            );
            setViewId(accessResult.view_id);
            const token = accessResult.access_token || null;
            setAccessToken(token);

            // Load the full data — pass token as header fallback for cross-site cookie blocking
            const dataResult = await getShareData(shareId, token);
            setData(dataResult);
            setCachedShareData(shareId, dataResult);
            setAccessGranted(true);
        } catch (err) {
            debug.error('Access validation failed:', err);
            setAccessError(err.message || 'Access denied. Please check your credentials.');
        } finally {
            setAccessLoading(false);
        }
    };

    // Determine content based on current state
    let content;
    if (loading) {
        content = <LoadingState message="Loading shared page..." />;
    } else if (error) {
        content = <ErrorState error={error} />;
    } else if (!accessGranted) {
        content = (
            <AccessGate
                meta={meta}
                password={password}
                setPassword={setPassword}
                email={email}
                setEmail={setEmail}
                accessError={accessError}
                accessLoading={accessLoading}
                onSubmit={handleAccessSubmit}
            />
        );
    } else {
        content = <SharedContent type={type} meta={meta} data={data} />;
    }

    // Wrap in theme provider to ensure consistent branding
    return (
        <ThemeProvider theme={sharedPageTheme}>
            <CssBaseline />
            {content}
        </ThemeProvider>
    );
}
